import React, { useState, useEffect, useMemo } from 'react';

import { useTranslation } from 'react-i18next';
import { Flex } from 'design-system-web';
import { WatchlistSeverityLevel, WatchlistLevelConfig } from 'graphql-query-contracts';
import { PillColor } from 'application-types';
import useChannelGroupRolesQuery from 'lane-shared/hooks/useChannelGroupRolesQuery';
import { useFlag } from 'lane-shared/hooks';
import { FeatureFlag } from 'constants-flags';
import { convertToUUID } from 'uuid-encoding';
import { WatchlistLevelInput } from '../WatchlistLevelInput/WatchlistLevelInput';
import { useWatchlistLevelConfigs } from '../../hooks/useWatchlistLevelConfigs';

import styles from './WatchlistLevelsPanel.scss';

type WatchlistLevelsPanelProps = {
  channelId: string;
  onFieldUpdate?: (levels: WatchlistLevelConfig[]) => void;
  configs?: WatchlistLevelConfig[];
  showValidationErrors?: boolean;
};

const SEVERITY_ORDER = [
  WatchlistSeverityLevel.WatchlistSeverityLevelHigh,
  WatchlistSeverityLevel.WatchlistSeverityLevelMedium,
  WatchlistSeverityLevel.WatchlistSeverityLevelLow,
];

const PILL_COLORS: Record<WatchlistSeverityLevel, PillColor> = {
  [WatchlistSeverityLevel.WatchlistSeverityLevelHigh]: 'red',
  [WatchlistSeverityLevel.WatchlistSeverityLevelMedium]: 'honey',
  [WatchlistSeverityLevel.WatchlistSeverityLevelLow]: 'gray',
  [WatchlistSeverityLevel.WatchlistSeverityLevelInvalid]: 'gray',
};

const TEST_IDS: Record<WatchlistSeverityLevel, string> = {
  [WatchlistSeverityLevel.WatchlistSeverityLevelHigh]: 'high-risk-level',
  [WatchlistSeverityLevel.WatchlistSeverityLevelMedium]: 'medium-risk-level',
  [WatchlistSeverityLevel.WatchlistSeverityLevelLow]: 'low-risk-level',
  [WatchlistSeverityLevel.WatchlistSeverityLevelInvalid]: 'unknown-risk-level',
};

export function WatchlistLevelsPanel({ 
  channelId, 
  onFieldUpdate,
  configs: configsFromProps,
  showValidationErrors = false,
}: WatchlistLevelsPanelProps): React.ReactElement {
  const { t } = useTranslation();
  const { data: configsFromHook } = useWatchlistLevelConfigs(channelId);
  const { groupRoles } = useChannelGroupRolesQuery({
    channelId,
    includeWorkplaceMember: false,
  });
  const [levelsState, setLevelsState] = useState<WatchlistLevelConfig[]>([]);
  const isTeamIdFormatFixEnabled = useFlag(FeatureFlag.WatchlistLevelTeamIdFormatFix, false);

  const availableTeams = useMemo(
    () =>
      groupRoles.map(role => ({
        value: isTeamIdFormatFixEnabled ? convertToUUID(role._id) : role._id,
        label: role.name,
      })),
      [groupRoles, isTeamIdFormatFixEnabled]
    );
    
  useEffect(() => {
    // Use configs from props (unsaved changes) if available, otherwise use hook data
    if (configsFromProps?.length) {
      setLevelsState(configsFromProps);
    } else if (configsFromHook?.length) {
      setLevelsState(configsFromHook);
    }
  }, [configsFromHook, configsFromProps]);

  const updateLevel = (severityLevel: WatchlistSeverityLevel, updates: Partial<WatchlistLevelConfig>) => {
    setLevelsState(currentConfigs => {
      const updated = currentConfigs.map(config => {
        if (config.level === severityLevel) {
          return { ...config, ...updates };
        }

        return config;
      });

      onFieldUpdate?.(updated);

      return updated;
    });
  };

  const sortedConfigs = SEVERITY_ORDER.flatMap(severityLevel => {
    const config = levelsState.find(config => config.level === severityLevel);

    return config ? [config] : [];
  });

  const getTeamSelectionError = (config: WatchlistLevelConfig): string[] | undefined => {
    if (showValidationErrors && config.isNotificationsEnabled && config.notificationTargets.length === 0) {
      return [t('web.admin.visitorManagement.configuration.watchlistLevel.selectTeamsError')];
    }

    return undefined;
  };

  return (
    <Flex direction="row" gap={5} className={styles.levelsContainer}>
      {sortedConfigs.map(config => (
        <WatchlistLevelInput
          key={config.level}
          severityLevel={config.level}
          displayLevelText={config.displayLevelText}
          onDisplayLevelTextChange={name => updateLevel(config.level, { displayLevelText: name })}
          pillColor={PILL_COLORS[config.level]}
          isNotificationsEnabled={config.isNotificationsEnabled}
          onIsNotificationsEnabledChange={enabled => updateLevel(config.level, { isNotificationsEnabled: enabled })}
          availableTeams={availableTeams}
          notificationTargets={isTeamIdFormatFixEnabled ? config.notificationTargets.map(id => convertToUUID(id)) : config.notificationTargets}
          onNotificationTargetsChange={teams => updateLevel(config.level, { notificationTargets: teams })}
          isWatchlistDisplayed={config.isWatchlistDisplayed}
          onIsWatchlistDisplayedChange={enabled => updateLevel(config.level, { isWatchlistDisplayed: enabled })}
          isApprovalRequired={config.isApprovalRequired}
          onIsApprovalRequiredChange={enabled => updateLevel(config.level, { isApprovalRequired: enabled })}
          testId={TEST_IDS[config.level]}
          teamSelectionError={getTeamSelectionError(config)}
        />
      ))}
    </Flex>
  );
}

